import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Calendar, User, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PendingCheckout } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface PendingCheckoutsWidgetProps {
  checkouts: PendingCheckout[];
  loading?: boolean;
}

const PendingCheckoutsWidget: React.FC<PendingCheckoutsWidgetProps> = ({
  checkouts,
  loading = false
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Calculate pagination
  const totalPages = Math.ceil(checkouts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCheckouts = checkouts.slice(startIndex, endIndex);
  
  // Reset to first page when checkouts change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [checkouts.length]);

  if (loading) {
    return (
      <div className="h-full">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {checkouts.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground flex-1 flex flex-col justify-center">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhum check-out pendente</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="space-y-2 pr-1">
                {currentCheckouts.map((checkout) => (
                  <div
                    key={checkout.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors min-h-[80px]"
                    onClick={() => navigate(`/classes/${checkout.class.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-extrabold truncate">
                          {checkout.class.course.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 mb-1 text-xs text-muted-foreground">
                        {checkout.org_name && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {checkout.org_name}
                          </div>
                        )}
                        {checkout.instructor && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {checkout.instructor.first_name} {checkout.instructor.last_name}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(parseISO(checkout.start_date), "dd/MM 'às' HH:mm", { locale: ptBR })}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {checkout.duration_minutes}min
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 pt-3 border-t flex-shrink-0">
                <Pagination>
                  <PaginationContent className="justify-center">
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PendingCheckoutsWidget;
